import { useState, useEffect, useRef } from 'react';
import { CasinoService } from 'services/casino';
import { timeout, retry, catchError, of, forkJoin } from 'rxjs';
import { IListResponse } from '@interfaces/services';
import { FloorPlan, IState, SlotMetaData } from 'appRedux/createStore';
import { useSelector } from 'react-redux';
import { hasFloorPlanFeatures, isInsightCasinoSchema } from 'services/insightCasino';
import { hasCasinoS3Files } from 'components/FloorMapPackage/data/geoJson';

const useFetchGeoJson = (casinoId: number) => {
  const casinoSvc = new CasinoService();
  const [geoJsonData, setGeoJsonData] = useState<IListResponse<FloorPlan>>();
  const [slotMetaData, setSlotMetaData] = useState<IListResponse<SlotMetaData>>();
  const [getGeoJsonLoading, setGeoJsonLoading] = useState(false);
  const [slotMetaDataLoading, setSlotMetaDataLoading] = useState(false);
  const prevCasinoIdRef = useRef<string>();
  const casinoSchema = useSelector((state: IState) => state.app.persistedState.casinoSchema);
  // Gate ONLY on the casino object's insightInstance field (no casino ids).
  const isInsight = isInsightCasinoSchema(casinoSchema);
  // Insight casinos: the map renders only when GetGeoJSON returns features.
  const [insightHasFloorPlan, setInsightHasFloorPlan] = useState(false);
  // Insight casinos: GetGeoJSON timed out / failed (drives a small retry hint).
  const [geoJsonError, setGeoJsonError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    const key = `${casinoId}:${isInsight ? 1 : 0}:${retryNonce}`;
    if (prevCasinoIdRef.current === key) return;
    prevCasinoIdRef.current = key;

    if (isInsight) {
      // Insight casinos never call GetMapMetaData: machine placement (section,
      // bank, location, floorLocation, onMap) comes from the GetCasinoInsights
      // rows. GetGeoJSON is always called; an empty FeatureCollection or an
      // error means list-only Explore.
      setGeoJsonData(undefined);
      setSlotMetaData(undefined);
      setSlotMetaDataLoading(false);
      setInsightHasFloorPlan(false);
      setGeoJsonError(false);
      setGeoJsonLoading(true);
      const geoSub = casinoSvc
        .GetGeoJSON(casinoId)
        .pipe(
          timeout(30000),
          retry(1),
          catchError((err) => {
            console.error('GeoJSON fetch error:', err);
            setGeoJsonError(true);
            return of(undefined);
          })
        )
        .subscribe((geoJsonResponse) => {
          const hasFeatures = hasFloorPlanFeatures(geoJsonResponse || null);
          if (geoJsonResponse && hasFeatures) setGeoJsonData(geoJsonResponse);
          setInsightHasFloorPlan(hasFeatures || hasCasinoS3Files(casinoId));
          setGeoJsonLoading(false);
        });
      return () => geoSub.unsubscribe();
    }

    setGeoJsonLoading(true);
    setSlotMetaDataLoading(true);

    const geoJson$ = casinoSvc.GetGeoJSON(casinoId).pipe(
      timeout(30000),
      retry(1),
      catchError((err) => {
        console.error('GeoJSON fetch error:', err);
        return of(undefined);
      })
    );

    const metaData$ = casinoSvc.GetMapMetaData(casinoId).pipe(
      timeout(30000),
      retry(1),
      catchError((err) => {
        console.error('MetaData fetch error:', err);
        return of(undefined);
      })
    );

    const subscription = forkJoin([geoJson$, metaData$]).subscribe(
      ([geoJsonResponse, metaDataResponse]) => {
        if (geoJsonResponse) setGeoJsonData(geoJsonResponse);
        if (metaDataResponse) setSlotMetaData(metaDataResponse);
        setGeoJsonLoading(false);
        setSlotMetaDataLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, [casinoId, isInsight, retryNonce]);

  return {
    geoJsonData,
    getGeoJsonLoading,
    slotMetaData,
    slotMetaDataLoading,
    // Insight casino whose GetGeoJSON has no features (or failed): list only.
    // Also true while GeoJSON is still loading, so the map layout never paints
    // first for an insight casino.
    isMapless: isInsight && !insightHasFloorPlan,
    isInsightGeoJsonLoading: isInsight && getGeoJsonLoading,
    geoJsonError: isInsight && geoJsonError,
    retryGeoJson: () => setRetryNonce((n) => n + 1),
  };
};

export default useFetchGeoJson;
